import { useLayoutEffect, useState } from 'react'

import type { Temporal } from '@js-temporal/polyfill'
import clsx from 'clsx'

import { useResizeObserver } from '@buffer-mono/popcorn'

import { format, isSameDay } from '~publish/helpers/temporal'

import { useCalendarContext } from './context'

import styles from './Header.module.css'

import type { JSX, ReactNode } from 'react'

type HeaderProps = {
  startDate: Temporal.ZonedDateTime
  withDay: boolean
  numberOfDays?: number
  className?: string
  progress?: ReactNode
}

export const Header = ({
  startDate,
  withDay,
  numberOfDays = 7,
  className,
  progress,
}: HeaderProps): JSX.Element => {
  const {
    currentTime: { today },
    isMobile,
  } = useCalendarContext()
  const days = Array.from({ length: numberOfDays }, (_, i) =>
    startDate.add({ days: i }),
  )
  const [rowRef, { width: rowWidth }] = useResizeObserver<HTMLTableRowElement>()
  const columnWidth =
    rowWidth === undefined ? undefined : rowWidth / numberOfDays
  const [maxAbbreviatedWidth, setMaxAbbreviatedWidth] = useState<number>()
  const isAbbreviated =
    isMobile ||
    (columnWidth !== undefined &&
      maxAbbreviatedWidth !== undefined &&
      columnWidth <= maxAbbreviatedWidth)

  useLayoutEffect(() => {
    const row = rowRef.current
    if (isAbbreviated || columnWidth === undefined || !row) return
    const isClipped = Array.from(
      row.querySelectorAll<HTMLElement>(`.${styles.dayName}`),
    ).some((name) => name.scrollWidth > name.clientWidth)
    if (isClipped) setMaxAbbreviatedWidth(columnWidth)
  }, [rowRef, isAbbreviated, columnWidth])

  const weekdayFormat = isAbbreviated ? 'EEE' : 'EEEE'
  return (
    <thead className={styles.stickyHeader}>
      <tr ref={rowRef} className={clsx(styles.headerRow, className)}>
        {days.map((day) => {
          const isActive = isSameDay(day, today) && withDay
          return (
            <th
              key={day.toString()}
              className={clsx(styles.day, {
                [styles.active]: isActive,
              })}
            >
              {withDay ? (
                <div className={styles.dayLabelContainer}>
                  <span className={styles.dayLabel}>
                    {/* The space keeps the cell's accessible name as "Wednesday 3";
                        flex layout drops the whitespace-only node, so .dayLabel's
                        gap sets the visible spacing. */}
                    <span className={styles.dayName}>
                      {format(day, weekdayFormat)}
                    </span>{' '}
                    <span className={styles.dayNumber}>{format(day, 'd')}</span>
                  </span>
                </div>
              ) : (
                <span className={styles.dayName}>
                  {format(day, weekdayFormat)}
                </span>
              )}
            </th>
          )
        })}
      </tr>
      {progress && (
        <tr className={styles.progressRow} aria-hidden="true">
          <th className={styles.progressCell} colSpan={numberOfDays}>
            {progress}
          </th>
        </tr>
      )}
    </thead>
  )
}
